import { Fragment } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { ROUTES } from "../lib/routes";
import { HOSTELS, totalBoarders, totalRooms } from "../lib/hostels";

const allBlocks = HOSTELS.reduce((n, h) => n + h.blocks.length, 0);
const allRooms = HOSTELS.reduce((n, h) => n + totalRooms(h), 0);
const allBoarders = HOSTELS.reduce((n, h) => n + totalBoarders(h), 0);
const firstEstd = Math.min(...HOSTELS.map((h) => h.estd));

/**
 * `count` runs through the counter animation, which thousands-separates its
 * value — right for quantities, wrong for a year (1984 became "1,984"), so the
 * year is rendered as a literal instead.
 */
const stats: Array<{ count?: number; literal?: string; lbl: string }> = [
  { count: allBoarders, lbl: "Boarders across all blocks" },
  { count: allBlocks, lbl: "Residential blocks" },
  { count: allRooms, lbl: "Rooms" },
  { literal: String(firstEstd), lbl: "First hostel opened" },
];

/** Hero image: the Amrutha block exterior, the only published building shot. */
const heroMedia = HOSTELS.find((h) => h.photos?.length)?.photos?.[0] ?? null;

export default function Hostels() {
  return (
    <>
      <PageHero
        variant="hero-photo"
        eyebrow="Hostels & Mess"
        title="Two campus hostels, boys' and ladies'."
        lead="BIET's boys' hostel has been running since 1984 and the Amrutha ladies' hostel since 2008. Both are managed by resident wardens and deputy wardens, run their own mess, and sit on campus within reach of the academic blocks."
        crumbs={[{ label: "Campus Life", to: ROUTES.campusLife }, { label: "Hostels" }]}
        heroMedia={heroMedia}
        image="HOSTEL · AMRUTHA BLOCK"
      />
      <section className="section">
        <div className="container">
          <div className="kicker">At a glance</div>
          <div className="stat-row">
            {stats.map((s, i) => (
              <div key={i} className="cell reveal">
                {s.literal !== undefined ? (
                  <div className="num">{s.literal}</div>
                ) : (
                  <div className="num" data-count={s.count}>0</div>
                )}
                <div className="lbl">{s.lbl}</div>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 14, fontSize: 12, color: "var(--text-muted)" }}>
            Counts are the sum of the blocks each hostel itemises. The boys' hostel separately
            describes three blocks and about 510 boarders, one of which it does not break out.
          </p>

          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>The two hostels</h2>
          <p style={{ color: "var(--text-soft)", marginTop: 8, fontSize: 17, maxWidth: "62ch" }}>
            Each has its own warden team, mess and admission procedure. Open one for its blocks,
            amenities, fees, room rules and contacts.
          </p>
          <div className="er-grid" style={{ marginTop: "var(--space-4)" }}>
            {HOSTELS.map((h) => (
              <Link key={h.slug} to={`${ROUTES.hostelDetail}/${h.slug}`} className="er-card">
                <div className="er-date">
                  <span className="er-day">{totalBoarders(h)}</span>
                  <span className="er-month">BOARDERS</span>
                </div>
                <div className="er-body">
                  <span className="er-tag">{h.gender}</span>
                  <h3>{h.name}</h3>
                  <span className="er-venue">
                    Since {h.estd} · {h.blocks.map((b) => b.name).join(", ")}
                  </span>
                </div>
                <span className="er-arr" aria-hidden>→</span>
              </Link>
            ))}
          </div>

          {/* ─── Fees ─────────────────────────────────────── */}
          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>Fees</h2>
          <table className="fee-table">
            <thead>
              <tr>
                <th>Hostel</th>
                <th>Published for</th>
                <th style={{ textAlign: "right" }}>Fee incl. mess</th>
                <th style={{ textAlign: "right" }}>Deposit</th>
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {HOSTELS.map((h) => (
                <tr key={h.slug}>
                  <td style={{ fontFamily: "var(--font-serif)", fontWeight: 600 }}>{h.name}</td>
                  <td style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", fontSize: 12 }}>{h.fee.year}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>{h.fee.rows[0].amount}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", color: "var(--text-muted)" }}>{h.fee.rows[1].amount}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{h.fee.total}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ─── Wardens ──────────────────────────────────── */}
          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>Wardens</h2>
          <div className="contact-grid" style={{ marginTop: "var(--space-4)" }}>
            {HOSTELS.map((h) => {
              const lead = h.contacts.filter((c) => c.role !== "Director" && c.role !== "Principal");
              return (
                <div key={h.slug} className="contact-block">
                  <div className="ctype">{h.gender} hostel</div>
                  <h3>{h.name}</h3>
                  <dl className="row">
                    {lead.map((c, i) => (
                      <Fragment key={i}>
                        <dt>{c.role}</dt>
                        <dd>
                          {c.name}
                          {c.phone && (
                            <> · <a href={`tel:+91${c.phone}`}>{c.phone}</a></>
                          )}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>

          {/* ─── Gallery ──────────────────────────────────── */}
          {(() => {
            const gallery = HOSTELS.flatMap((h) => h.photos ?? []);
            if (gallery.length === 0) return null;
            return (
              <>
                <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>Inside the hostels</h2>
                <div
                  style={{
                    marginTop: "var(--space-4)",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "var(--space-4)",
                  }}
                >
                  {gallery.map((p, i) => (
                    <figure key={i} style={{ margin: 0 }} className="reveal">
                      <Figure media={p} fallbackLabel="HOSTEL" aspect="2/1" sizes="(max-width: 760px) 100vw, 32vw" />
                      <figcaption
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: "var(--text-muted)",
                          marginTop: 10,
                        }}
                      >
                        {p.alt}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>
    </>
  );
}
